import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Truck, Clock, Shield, CheckCircle, MapPin, Phone, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';

const locationData = {
  reading: {
    name: 'Reading',
    postcode: 'RG1',
    description: 'Professional same-day delivery service in Reading, Berkshire. Fast, reliable courier service covering all RG postcodes.',
    areas: ['RG1', 'RG2', 'RG4', 'RG5', 'RG6', 'RG7', 'RG8', 'RG10', 'RG30', 'RG31'],
    landmarks: ['Reading Station', 'The Oracle Shopping Centre', 'Forbury Gardens', 'Reading University', 'Thames Valley Park'],
    distance: '0-5 miles from city centre',
    seoTitle: 'Same Day Delivery Reading | Fast Courier Service RG Postcodes | AIS Express',
    seoDescription: 'Same day delivery in Reading from £20. Professional B2B courier covering all RG postcodes. 50k£ insurance, 98% on-time. Book now: 01344 508095'
  },
  bracknell: {
    name: 'Bracknell',
    postcode: 'RG12',
    description: 'Reliable same-day delivery service in Bracknell, Berkshire. Covering all RG12 postcodes with professional courier service.',
    areas: ['RG12', 'RG40', 'RG42', 'RG45'],
    landmarks: ['The Lexicon', 'Bracknell Town Centre', 'Coral Reef Waterworld', 'South Hill Park'],
    distance: '0-3 miles from town centre',
    seoTitle: 'Same Day Delivery Bracknell | RG12 Courier Service | AIS Express',
    seoDescription: 'Same day delivery in Bracknell from £20. Fast courier service covering RG12, RG40, RG42 postcodes. 50k£ insurance. Call 01344 508095'
  },
  slough: {
    name: 'Slough',
    postcode: 'SL1',
    description: 'Fast same-day delivery service in Slough, Berkshire. Professional courier covering all SL postcodes.',
    areas: ['SL1', 'SL2', 'SL3', 'SL4', 'SL6', 'SL7', 'SL8', 'SL9'],
    landmarks: ['Slough Trading Estate', 'Queensmere Shopping Centre', 'Heathrow Airport proximity', 'M4 Corridor'],
    distance: '0-4 miles from town centre',
    seoTitle: 'Same Day Delivery Slough | SL Postcode Courier Service | AIS Express',
    seoDescription: 'Same day delivery in Slough from £20. Professional B2B courier covering all SL postcodes. 98% on-time delivery. Book: 01344 508095'
  },
  newbury: {
    name: 'Newbury',
    postcode: 'RG14',
    description: 'Professional same-day delivery service in Newbury, Berkshire. Fast courier service covering RG14, RG18, RG20 postcodes.',
    areas: ['RG14', 'RG18', 'RG20'],
    landmarks: ['Newbury Racecourse', 'Parkway Shopping', 'Vodafone Headquarters', 'Newbury Business Park'],
    distance: '0-5 miles from town centre',
    seoTitle: 'Same Day Delivery Newbury | RG14 Courier Service | AIS Express',
    seoDescription: 'Same day delivery in Newbury from £20. Covering RG14, RG18, RG20. Professional courier with 50k£ insurance. Call 01344 508095'
  },
  wokingham: {
    name: 'Wokingham',
    postcode: 'RG40',
    description: 'Same-day delivery service in Wokingham, Berkshire. Reliable courier covering RG40 and RG41 postcodes.',
    areas: ['RG40', 'RG41'],
    landmarks: ['Wokingham Town Hall', 'The Rose Street', 'Elms Field Shopping Centre', 'Wokingham Station'],
    distance: '0-3 miles from town centre',
    seoTitle: 'Same Day Delivery Wokingham | RG40 Courier Service | AIS Express',
    seoDescription: 'Same day delivery in Wokingham from £20. Fast courier service for RG40, RG41. 98% on-time rate. Book now: 01344 508095'
  },
  windsor: {
    name: 'Windsor',
    postcode: 'SL4',
    description: 'Premium same-day delivery service in Windsor, Berkshire. Professional courier covering Windsor Castle area and SL4 postcodes.',
    areas: ['SL4', 'SL5'],
    landmarks: ['Windsor Castle', 'Eton College', 'Windsor Guildhall', 'Royal Windsor Racecourse'],
    distance: '0-3 miles from Windsor Castle',
    seoTitle: 'Same Day Delivery Windsor | SL4 Courier Service | AIS Express',
    seoDescription: 'Same day delivery in Windsor from £20. Professional courier covering Windsor & Eton. 50k£ insurance. Call 01344 508095'
  },
  maidenhead: {
    name: 'Maidenhead',
    postcode: 'SL6',
    description: 'Fast same-day delivery service in Maidenhead, Berkshire. Professional courier covering SL6 postcodes.',
    areas: ['SL6', 'SL7'],
    landmarks: ['Maidenhead Station', 'Nicholsons Shopping Centre', 'Braywick Park', 'Maidenhead Town Centre'],
    distance: '0-4 miles from town centre',
    seoTitle: 'Same Day Delivery Maidenhead | SL6 Courier Service | AIS Express',
    seoDescription: 'Same day delivery in Maidenhead from £20. Covering SL6, SL7. Professional B2B courier. 98% on-time. Book: 01344 508095'
  },
  basingstoke: {
    name: 'Basingstoke',
    postcode: 'RG21',
    description: 'Professional same-day delivery service in Basingstoke, Hampshire. Fast, reliable courier covering all RG21-RG29 postcodes.',
    areas: ['RG21', 'RG22', 'RG23', 'RG24', 'RG25', 'RG26', 'RG27', 'RG28', 'RG29'],
    landmarks: ['Festival Place', 'Basingstoke Station', 'Chineham Shopping Centre', 'Hampshire Clinic', 'Leisure Park'],
    distance: '0-5 miles from town centre',
    seoTitle: 'Same Day Delivery Basingstoke | RG21 Courier Service Hampshire | AIS Express',
    seoDescription: 'Same day courier Basingstoke from £20. Covering all RG21-RG29 postcodes. Urgent delivery near you. 50k£ insured. Call 01344 508095'
  },
  farnborough: {
    name: 'Farnborough',
    postcode: 'GU14',
    description: 'Fast same-day delivery service in Farnborough, Hampshire. Professional courier covering GU14 and surrounding areas near Farnborough Airport.',
    areas: ['GU14', 'GU11', 'GU12', 'GU51', 'GU52'],
    landmarks: ['Farnborough Airport', 'Princes Mead Shopping Centre', 'Farnborough Main Station', 'Farnborough Business Park'],
    distance: '0-4 miles from town centre',
    seoTitle: 'Same Day Delivery Farnborough | GU14 Courier Service | AIS Express',
    seoDescription: 'Same day courier in Farnborough from £20. Covering GU14, GU11, GU12. Near Farnborough Airport. 98% on-time. Call 01344 508095'
  },
  camberley: {
    name: 'Camberley',
    postcode: 'GU15',
    description: 'Reliable same-day delivery service in Camberley, Surrey. Professional courier covering GU15, GU16, GU17 postcodes.',
    areas: ['GU15', 'GU16', 'GU17', 'GU18', 'GU19', 'GU20'],
    landmarks: ['The Mall Camberley', 'Camberley Station', 'Frimley Park Hospital', 'Blackdown Barracks'],
    distance: '0-3 miles from town centre',
    seoTitle: 'Same Day Delivery Camberley | GU15 Courier Service Surrey | AIS Express',
    seoDescription: 'Same day courier Camberley from £20. Covering GU15-GU20 postcodes. Urgent delivery near me. 50k£ insured. Call 01344 508095'
  },
  ascot: {
    name: 'Ascot',
    postcode: 'SL5',
    description: 'Premium same-day delivery service in Ascot, Berkshire. Professional courier covering SL5 postcodes and Ascot Racecourse area.',
    areas: ['SL5'],
    landmarks: ['Ascot Racecourse', 'Sunninghill', 'Royal Ascot', 'Ascot High Street'],
    distance: '0-3 miles from Ascot High Street',
    seoTitle: 'Same Day Delivery Ascot | SL5 Courier Service | AIS Express',
    seoDescription: 'Same day courier in Ascot from £20. Covering SL5 postcodes. Discreet, professional delivery. 50k£ insurance. Call 01344 508095'
  },
  woodley: {
    name: 'Woodley',
    postcode: 'RG5',
    description: 'Fast same-day delivery service in Woodley, Berkshire. Professional courier covering RG5 and surrounding Reading suburbs.',
    areas: ['RG5', 'RG6', 'RG10'],
    landmarks: ['Woodley Precinct', 'Woodford Park', 'Woodley Airfield', 'Loddon Vale'],
    distance: '0-3 miles from Woodley centre',
    seoTitle: 'Same Day Delivery Woodley | RG5 Courier Service | AIS Express',
    seoDescription: 'Same day courier Woodley from £20. Covering RG5, RG6, RG10. Fast local delivery near Reading. Call 01344 508095'
  },
  'henley-on-thames': {
    name: 'Henley-on-Thames',
    postcode: 'RG9',
    description: 'Professional same-day delivery service in Henley-on-Thames, Oxfordshire. Fast courier covering RG9 postcodes along the Thames.',
    areas: ['RG9'],
    landmarks: ['Henley Regatta', 'Henley Bridge', 'River & Rowing Museum', 'Henley Market Place'],
    distance: '0-3 miles from town centre',
    seoTitle: 'Same Day Delivery Henley-on-Thames | RG9 Courier Service | AIS Express',
    seoDescription: 'Same day courier Henley-on-Thames from £20. Covering RG9 postcodes. Reliable delivery along the Thames. Call 01344 508095'
  }
};

export const LocationPage = () => {
  const { location } = useParams();
  const navigate = useNavigate();
  const data = locationData[location?.toLowerCase()];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Location Not Found</h2>
            <p className="text-slate-600 mb-6">We couldn't find information for this location.</p>
            <Button onClick={() => navigate('/')}>Go to Homepage</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": `Same Day Delivery ${data.name}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "AIS Express",
      "telephone": "+44-1344-508095",
      "areaServed": {
        "@type": "City",
        "name": data.name,
        "addressRegion": "Berkshire",
        "addressCountry": "GB"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": data.name
    }
  };

  return (
    <>
      <Helmet>
        <title>{data.seoTitle}</title>
        <meta name="description" content={data.seoDescription} />
        <meta name="keywords" content={`same day delivery ${data.name.toLowerCase()}, same day delivery near me ${data.name.toLowerCase()}, courier ${data.name.toLowerCase()}, courier service near me, ${data.postcode} delivery, urgent delivery ${data.name.toLowerCase()}, same day courier ${data.name.toLowerCase()}, cheap courier ${data.name.toLowerCase()}, next day delivery ${data.name.toLowerCase()}, man and van ${data.name.toLowerCase()}, parcel delivery ${data.name.toLowerCase()}, business courier ${data.name.toLowerCase()}, B2B courier ${data.name.toLowerCase()}, express delivery ${data.name.toLowerCase()}, reliable courier near me`} />
        <link rel="canonical" href={`https://aisexpress.co.uk/location/${location.toLowerCase()}`} />
        <link rel="alternate" hrefLang="en-GB" href={`https://aisexpress.co.uk/location/${location.toLowerCase()}`} />
        <meta property="og:title" content={data.seoTitle} />
        <meta property="og:description" content={data.seoDescription} />
        <meta property="og:url" content={`https://aisexpress.co.uk/location/${location.toLowerCase()}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_GB" />
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center space-x-2 text-emerald-200 mb-4">
                <MapPin className="h-5 w-5" />
                <span className="text-sm font-medium">{data.postcode} Area</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Same Day Delivery in {data.name}
              </h1>
              
              <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                {data.description}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Button
                  onClick={() => navigate('/book')}
                  size="lg"
                  className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold"
                >
                  <Truck className="mr-2 h-5 w-5" />
                  Book Delivery Now
                </Button>
                <Button
                  onClick={() => window.location.href = 'tel:01344508095'}
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10"
                >
                  <Phone className="mr-2 h-5 w-5" />
                  Call 01344 508095
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <Shield className="h-6 w-6 text-emerald-300 mb-2" />
                  <p className="text-sm font-medium">50k£ Insurance</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <Clock className="h-6 w-6 text-emerald-300 mb-2" />
                  <p className="text-sm font-medium">Same-Day Service</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-emerald-300 mb-2" />
                  <p className="text-sm font-medium">98% On-Time</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm p-4 rounded-lg">
                  <Truck className="h-6 w-6 text-emerald-300 mb-2" />
                  <p className="text-sm font-medium">24/7 Available</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Coverage Area */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <Card className="shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-slate-900 mb-6">
                    Coverage in {data.name}
                  </h2>
                  
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center">
                        <MapPin className="h-5 w-5 text-emerald-600 mr-2" />
                        Postcodes We Cover
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {data.areas.map((area, index) => (
                          <span
                            key={index}
                            className="bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {area}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-slate-800 mb-4">
                        Key Locations
                      </h3>
                      <ul className="space-y-2">
                        {data.landmarks.map((landmark, index) => (
                          <li key={index} className="flex items-center text-slate-700">
                            <CheckCircle className="h-4 w-4 text-emerald-600 mr-2 flex-shrink-0" />
                            {landmark}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-emerald-50 p-6 rounded-lg border border-emerald-200">
                    <p className="text-slate-700">
                      <strong>Service Radius:</strong> {data.distance} • 
                      <strong className="ml-2">Response Time:</strong> Collection within 1 hour • 
                      <strong className="ml-2">Available:</strong> 24/7
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
                Delivery Services in {data.name}
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                <Card className="hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <Clock className="h-12 w-12 text-emerald-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-3">Same-Day Delivery</h3>
                    <p className="text-slate-600 mb-4">
                      Collection and delivery within {data.name} on the same day. Perfect for urgent documents and parcels.
                    </p>
                    <p className="text-emerald-600 font-semibold">From £20</p>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <Truck className="h-12 w-12 text-emerald-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-3">Urgent Delivery</h3>
                    <p className="text-slate-600 mb-4">
                      Express delivery within 0-6 hours. Dedicated vehicle for time-critical deliveries.
                    </p>
                    <p className="text-emerald-600 font-semibold">From £40</p>
                  </CardContent>
                </Card>

                <Card className="hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <Shield className="h-12 w-12 text-emerald-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-3">Business Delivery</h3>
                    <p className="text-slate-600 mb-4">
                      B2B courier service with 50k£ goods in transit insurance. Perfect for valuable items.
                    </p>
                    <p className="text-emerald-600 font-semibold">Insured up to £50k</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-emerald-600 to-emerald-700">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Book Your Delivery in {data.name}?
            </h2>
            <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
              Get instant quote or call us now for same-day collection
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/book')}
                size="lg"
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold"
              >
                Get Instant Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                onClick={() => window.location.href = 'tel:01344508095'}
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                <Phone className="mr-2 h-5 w-5" />
                01344 508095
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
